import { CountryType } from '../country/country.type';
import { AddressType } from '../address/address.type';
import { CURRENT_STATUS } from '../enums/current-status.enum';
import { OrganizationType } from '../organization/organization.type';
import { LocationType } from '../location/location.type';
import { VerifiedEmailType } from '../verified-email/verified-email.type';
import { ExternalSystemInstanceType } from '../external-system-instance/external-system-instance.type';
import { CDRType } from '../cdr/cdr.type';
import { BaseCDRType } from '../cdr/base-cdr.type';
import { TcServiceType } from '../tc-service/tc-service.type';
import { PortRequestType } from '../port-request/port-request.type';
import { PBXExtensionType } from '../pbx-extension/pbx-extension.type';
import { SimpleCountryType } from '../country/SimpleCountry.type';
import { SimpleOrganizationType } from '../organization/SimpleOrganization.type';
import { DIDServiceType } from '../service/DIDService.type';
import { DIDExternalSystemInstanceType } from '../external-system-instance/SimpleExternalSystemInstance.type';
import { PRIType } from '../pri/pri.type';
import { ExternalSystemProvider } from '../../utils/constants';

export type DIDE911Type = {
  overwriteE911: boolean;
  overwriteAddress: string | null;
  mainE911: boolean;
  mainAddress: string | null;
  locationAddress: string | null;
  customAddress: string | null;
  customAddressObj: AddressType | null;
  e911_lat: string | null;
  e911_lon: string | null;
  e911_coordinates_enabled: boolean;
  overwriteError: string | null;
  mainError: string | null;
  locationObj: LocationType | null;
  overwriteE911Instance: {
    id: number;
    name: string;
    provider: ExternalSystemProvider;
    prefix?: string;
  } | null;
  mainE911Instance: {
    id: number;
    name: string;
    provider: ExternalSystemProvider;
    prefix?: string;
  } | null;
};

export enum SipRegistrationStatus {
  Registered = 'registered',
  Unregistered = 'unregistered',
  Unknown = 'unknown',
}

export type DidTargetAssignment = {
  pbx: { id: number; description: string } | null;
  pri: { id: number; description: string } | null;
};

export enum DidAssignmentKind {
  Pbx = 'pbx',
  Pri = 'pri',
  None = 'none',
}

export type DidAssignment = {
  description: string;
  kind: DidAssignmentKind;
};

export type DidAssignmentSource = {
  pbxExtension?: { pbx?: { description?: string | null } | null } | null;
  pri?: { description?: string | null } | null;
  targetAssignment?: DidTargetAssignment | null;
};

export type DIDType = {
  id: number;
  csrpId: number | null;
  gdms_id: number | null;
  did: string;
  is_temporary: boolean;
  status: number;
  status_update_date: string | null;
  provider: string;
  external_id: string;
  description: string;
  cnam: string | null;
  e911: boolean;
  e911_address: string | null;
  e911_lat: string | null;
  e911_lon: string | null;
  e911_coordinates_enabled: boolean;
  did_username: string;
  did_password: string;
  did_authid: string;
  is_system_did: boolean;
  current_status: CURRENT_STATUS;
  sipRegistrationStatus: SipRegistrationStatus;
  hostname: string | null;
  created_at: string;
  modified_at: string;
  rcf: string;
  dnis: string;

  e911_information: Array<{
    address: string | null;
    e911: boolean;
    error: string | null;
    esiId: number;
    endpointId?: string;
  }>;
  enable_voicemail: boolean;
  voicemail_pin: null | string;
  user_email: null | string;
  no_answer_timeout: number;
  temporary_did?: DIDType | null;
  country?: CountryType | null;
  e911_custom_address?: AddressType | null;
  port_request?: PortRequestType | null;
  pri?: PRIType | null;
  service?: TcServiceType | null;
  e911_notify_email?: VerifiedEmailType | null;
  organization?: OrganizationType | null;
  e911_error?: string | null;
  location?: LocationType | null;
  telco_instance?: ExternalSystemInstanceType | null;
  e911Instance?: ExternalSystemInstanceType | null;
  cdrs?: Array<CDRType>;
  baseCdrsFrom?: Array<BaseCDRType>;
  baseCdrsTo?: Array<BaseCDRType>;
  pbxExtension?: PBXExtensionType | null;
  save_faxes?: boolean;
  contract?: { id: number; name: string } | null;
  targetAssignment: DidTargetAssignment | null;
};

export type DIDDetailsType = DIDType;

export type DIDsListItemType = Pick<
  DIDType,
  | 'id'
  | 'did'
  | 'is_temporary'
  | 'status_update_date'
  | 'description'
  | 'e911'
  | 'current_status'
  | 'rcf'
  | 'dnis'
  | 'e911_error'
  | 'pbxExtension'
  | 'e911_information'
  | 'port_request'
> & {
  pri: PRIType | null;
  country: SimpleCountryType | null;
  organization: SimpleOrganizationType | null;
  temporary_did: null;
  service: DIDServiceType | null;
  contract: { id: number } | null;
  telco_instance: DIDExternalSystemInstanceType | null;
  created_at: string;
  createdBy: { id: number; firstname: string; lastname: string; email: string } | null;
  targetAssignment: DidTargetAssignment | null;
};

export type BuyDIDItemDTO = {
  did: string;
  carrierId?: number;
};

export type BuyDIDsDto = {
  omitBuy?: boolean;
  dids: Array<BuyDIDItemDTO>;
  telco: { id: number };
  organization: { id: number };
  pbxSystem?: { id: number };
  location?: { id: number } | null;
  pbx?: { id: number } | null;
  pri?: { id: number } | null;
  serviceType?: { id: number } | null;
  pbxExtension?: { id: number } | null;
  country?: { id: number } | null;
};

export type CreateBYODIDDto = {
  location: { id: number };
  pbxSystem: { id: number };
};

export type CreateDIDDto = {
  did: string;
  country: { id: number } | null;
  description: string;
  assignTo: 'pri' | 'pbx' | 'none';

  // PRI Fields
  serviceType?: { id: number };
  dnis?: string;
  rcf?: string;

  // PBX Fields
  pbxExtension?: { id: number };
  extensionDescription?: string;
  extensionUsername?: string;
  extensionPassword?: string;
  extensionAuthId?: string;
};

// TODO: Define UpdateDIDDto
export type UpdateDIDDto = any;

export type DIDBulkRecord = {
  did: string;
  description: string;
  dial_code: string;
  did_authid: string;
  did_password: string;
  did_username: string;
  dnis: string;
  rcf: string;
};

export type CreateDIDs = {
  organization: { id: number };
  telcoInstance: { id: number };
  pbx?: { id: number };
  pri?: { id: number };
  dids: CreateDIDDto[];
};

export type ChangeAssignmentDto = {
  assignTo: 'pri' | 'pbx' | 'none';
  pbx?: { id: number };
  pbxExtension?: { id: number };
  pri?: { id: number };
  serviceType?: { id: number };
};

export type BulkChangeAssignmentDto = ChangeAssignmentDto & {
  ids: number[];
};

export type DeleteDIDOtherDidAction = 'delete' | 'disconnect';

export type DeleteDIDDto = {
  pbxExtension?: {
    id: number;
    otherDids: Array<{ id: number; action: DeleteDIDOtherDidAction }>;
  };
};
